import React from "react";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import update from "immutability-helper";
import { get, map, size, filter } from "lodash";

import Box from "@material-ui/core/Box";

import FormSection from "../../Common/FormSection";
import CustomButton from "../../Common/CustomButton";
import DatePicker from "../../Common/DatePicker";
import SitesFilter from "../SitesFilter";

import { nowWithOutSeconds } from "../../../utils/date";
import { getLov } from "../../../utils/lov";

const availablePeriods = [
  {
    name: "today",
    daysNumber: "0",
  },
  {
    name: "yesterday",
    daysNumber: 1,
  },
  {
    name: "last2days",
    daysNumber: 2,
  },
  {
    name: "last7days",
    daysNumber: 7,
  },
  {
    name: "custom",
    daysNumber: "custom",
  },
];

const zoneTypes = ["entrance", "exit"];

const styles = {
  gridLine: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, 200px)",
    gridAutoRows: "56px",
    gridGap: "24px",
    padding: "24px",
  },
};

const MdcForm = (props) => {
  const { classes, search, language } = props;
  const [lov, setLov] = React.useState({});

  const [filters, setFilters] = React.useState({
    zone_type: ["entrance", "exit"],
  });

  // Set initial filters
  React.useEffect(() => {
    initiateFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initiateFilters = async () => {
    try {
      let lov = {
        89: await getLov(89),
        tag: await getLov("tags"),
      };
      if (size(filter(lov[89], (i) => i.id === -1)) === 0) {
        lov[89].push({
          id: -1,
          name_en: I18n.get("site_noCarrier"),
          name_fr: I18n.get("site_noCarrier"),
        });
      }
      if (size(filter(lov.tag, (i) => i.id === -1)) === 0) {
        lov.tag.push({
          id: -1,
          name_en: I18n.get("site_noTag"),
          name_fr: I18n.get("site_noTag"),
        });
      }
      setLov(lov);

      const now = new Date();
      const midnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0
      );
      setFilters(
        update(filters, {
          dateFilter: { $set: "0" },
          startDate: { $set: midnight.getTime() },
          endDate: { $set: midnight.getTime() + 86400000 },
          //carrier: { $set: map(get(lov, [89]), (c) => c.id) },
          //tag: { $set: map(get(lov, ["tag"]), (c) => c.id) },
        })
      );
    } catch (e) {
      console.log(e);
    }
  };

  const handleChange = (id, index, value) => {
    if (id === "period") {
      if (value === "custom") {
        setFilters(
          update(filters, {
            dateFilter: { $set: "custom" },
            startDate: { $set: nowWithOutSeconds() - 86400000 },
            endDate: { $set: nowWithOutSeconds() },
          })
        );
      } else {
        const now = new Date();
        const midnight = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0,
          0,
          0
        );
        const startDate =
          value === "0"
            ? midnight.getTime()
            : new Date(midnight - 86400000 * value).getTime();
        const endDate =
          value === "0" ? midnight.getTime() + 86400000 : midnight.getTime();

        setFilters(
          update(filters, {
            dateFilter: { $set: value },
            startDate: { $set: startDate },
            endDate: { $set: endDate },
          })
        );
      }
    } else {
      setFilters(
        update(filters, {
          [id]: { $set: value },
        })
      );
    }
  };

  const handleChangeDate = (id, value) => {
    setFilters(
      update(filters, {
        [id]: { $set: new Date(value).getTime() },
      })
    );
  };

  return (
    <Box className={classes.gridLine}>
      {get(filters, ["startDate"]) && (
        <SitesFilter
          formValue={get(filters, ["sites"]) || []}
          setFormValue={handleChange}
          defaultSelectAll={true}
        />
      )}
      <Box>
        <FormSection
          formValue={get(filters, ["zone_type"]) || []}
          setFormValue={handleChange}
          title={"zoneTypes"}
          id={"zone_type"}
          type="select"
          options={{
            multipleSelect: true,
            options: map(zoneTypes, (t) => ({
              id: t,
              name: I18n.get(`dashboard_${t}`),
            })),
            clickThrough: true,
          }}
        />
      </Box>
      <Box>
        <FormSection
          formValue={get(filters, ["carrier"]) || []}
          setFormValue={handleChange}
          attribute={{ id: 89 }}
          id={"carrier"}
          type="select"
          options={{
            multipleSelect: true,
            selectAll: true,
            nullForAll: true,
            options: map(get(lov, [89]), (item) => ({
              id: get(item, ["id"]),
              name: get(item, [`name_${language}`]),
            })),
            clickThrough: true,
          }}
        />
      </Box>
      <Box>
        <FormSection
          formValue={get(filters, ["tag"]) || []}
          setFormValue={handleChange}
          title={"tags"}
          id={"tag"}
          type="select"
          options={{
            multipleSelect: true,
            selectAll: true,
            nullForAll: true,
            options: map(get(lov, ["tag"]), (item) => ({
              id: get(item, ["id"]),
              name: get(item, [`name_${language}`]),
            })),
            clickThrough: true,
          }}
        />
      </Box>
      <Box>
        <FormSection
          formValue={[get(filters, ["dateFilter"])]}
          setFormValue={handleChange}
          title={"period"}
          id={"period"}
          type="select"
          options={{
            noSort: true,
            options: map(availablePeriods, (p) => ({
              id: p.daysNumber,
              name: I18n.get(`dashboard_${p.name}`),
            })),
          }}
        />
      </Box>
      {get(filters, ["dateFilter"]) === "custom" && (
        <>
          <Box>
            <DatePicker
              label={I18n.get("site_startDate")}
              name="startDate"
              value={get(filters, ["startDate"])}
              onChange={handleChangeDate}
              width={200}
            />
          </Box>
          <Box>
            <DatePicker
              label={I18n.get("site_endDate")}
              name="endDate"
              value={get(filters, ["endDate"])}
              onChange={handleChangeDate}
              width={200}
            />
          </Box>
        </>
      )}
      <Box style={{ display: "flex", alignItems: "flex-end" }}>
        <CustomButton
          variant="contained"
          color="primary"
          onClick={() => search(filters)}
          style={{ marginTop: "4px" }}
        >
          {I18n.get("dashboard_search")}
        </CustomButton>
      </Box>
    </Box>
  );
};

export default withStyles(styles)(MdcForm);
